import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'

interface Comment {
  id: string
  text: string
  author: { id: string; full_name: string }
  created_at: string
}

interface Props {
  ideaId: string
  comments: Comment[]
}

export function CommentThread({ ideaId, comments }: Props) {
  const qc = useQueryClient()
  const [text, setText] = useState('')

  const mutation = useMutation({
    mutationFn: async (newText: string) =>
      api(`/api/ideas/${ideaId}/comments`, {
        method: 'POST',
        body: JSON.stringify({ text: newText }),
      }),
    onSuccess: () => {
      setText('')
      void qc.invalidateQueries({ queryKey: ['idea', ideaId] })
    },
  })

  return (
    <div className="space-y-4">
      <ul className="space-y-3">
        {comments.length === 0 && (
          <li className="text-sm text-zinc-500">Комментариев пока нет</li>
        )}
        {comments.map((c) => (
          <li
            key={c.id}
            className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg p-3"
          >
            <div className="flex justify-between items-baseline mb-1">
              <strong className="text-sm text-zinc-900 dark:text-zinc-100">
                {c.author.full_name}
              </strong>
              <span className="text-xs text-zinc-500">
                {new Date(c.created_at).toLocaleString('ru-RU')}
              </span>
            </div>
            <p className="text-sm text-zinc-700 dark:text-zinc-300 whitespace-pre-wrap">
              {c.text}
            </p>
          </li>
        ))}
      </ul>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (text.trim()) mutation.mutate(text.trim())
        }}
        className="space-y-2"
      >
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Добавить комментарий…"
          rows={3}
          className="w-full px-3 py-2 border border-zinc-300 dark:border-zinc-600 rounded-md bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 text-sm"
        />
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={!text.trim() || mutation.isPending}
            className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded-md text-sm"
          >
            {mutation.isPending ? 'Отправляю…' : 'Отправить'}
          </button>
        </div>
        {mutation.isError && (
          <p className="text-sm text-red-600">Не удалось отправить комментарий</p>
        )}
      </form>
    </div>
  )
}
